<script setup lang="ts">
import { adminProjectsService } from '@/services/admin/admin-projects-service.ts';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service.ts';
import { notificationService } from '@/services/notification/notification-service.ts';
import { PhFloppyDisk, PhPencilSimple } from '@phosphor-icons/vue';
import { models } from 'cesg-shared';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const projectName = ref<string>('');
const projectNumber = ref<string>('');
const isEditing = ref<boolean>(false);
const loading = ref<boolean>(false);

const emit = defineEmits<{
  (e: 'reloadProjects'): void;
}>();

const props = defineProps<{
  projectId: string | null;
  fortnoxId: string | null;
  isArchived: boolean | null;
}>();

const loadProjects = async (): Promise<void> => {
  if (!props.projectId) return;

  overlaySpinnerService.show();
  try {
    const project: models.AdminProjectResponse =
      await adminProjectsService.getProject(props.projectId);
    if (project) {
      projectName.value = project.name;
      projectNumber.value = project.fortnoxId;
    } else {
      console.warn(t('adminProjects.projectNotFound'));
    }
  } catch (error) {
    console.error(t('adminProjects.loadProjectFailed'), error);
  } finally {
    overlaySpinnerService.hide();
  }
};

const saveCurrentProject = async (): Promise<void> => {
  if (!props.projectId) return;

  loading.value = true;

  const body = {
    id: props.projectId,
    fortnoxId: projectNumber.value,
    name: projectName.value,
  };

  try {
    await adminProjectsService.updateProject(props.projectId, body);

    notificationService.open({
      text: t('adminProjects.projectSaveSuccessfully'),
      type: 'success',
    });

    emit('reloadProjects');
  } catch (error) {
    notificationService.open({
      text: t('adminProjects.failedToSaveProject'),
      type: 'danger',
    });
    console.error(t('adminProjects.failedToSaveProject'), error);
  } finally {
    isEditing.value = false;
    loading.value = false;
  }
};

onMounted(() => {
  loadProjects();
});

watch(
  () => props.projectId,
  (newProjectId) => {
    if (newProjectId) {
      loadProjects();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="project-header">
    <div>
      <template v-if="isEditing">
        <input v-model="projectName" />
      </template>
      <template v-else>
        <h3 class="title-project-number">{{ projectNumber }}</h3>
      </template>
    </div>

    <div>
      <template v-if="isEditing">
        <input v-model="projectNumber" />
      </template>
      <template v-else>
        <h3 class="title-project-name">{{ projectName }}</h3>
      </template>
    </div>

    <div class="button-group">
      <button
        v-if="!isEditing"
        @click="isEditing = true"
        title="Edit"
        :disabled="props.isArchived ?? false"
        :class="{ disabled: props.isArchived }"
      >
        <PhPencilSimple size="22" weight="bold" />
      </button>

      <button
        v-else
        @click="saveCurrentProject"
        title="Save"
        :disabled="props.isArchived ?? false"
        :class="{ disabled: props.isArchived }"
      >
        <PhFloppyDisk size="22" weight="bold" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.project-header {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
  padding: 1rem 0;
}

.title-project-name,
.title-project-number {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  background-color: #f0f4ff;
  color: #1a3d7c;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease-in-out;
}

input {
  font-size: 1.75rem;
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  border: 1px solid #c0d4f7;
  background-color: #f9fbff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  min-width: 10rem;
  transition: all 0.2s ease-in-out;
}

input:focus {
  outline: none;
  border-color: #1a3d7c;
  box-shadow: 0 0 0 3px rgba(26, 61, 124, 0.15);
}

.button-group {
  display: flex;
  gap: 0.6rem;
}

button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease-in-out;
}

button {
  background-color: #d6e2ff;
  color: #1a3d7c;
}

button:hover {
  background-color: #c0d2fa;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

button.save-btn {
  background-color: #c7e2c7;
  color: #2b612b;
}

button.save-btn:hover {
  background-color: #b3d7b3;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.delete-btn {
  background-color: #fdeaea;
  color: #b33939;
}

.delete-btn:hover {
  background-color: #f3c9c9;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

// Ta bort när knappen ska användas igen
.delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
